/**
 * Páginas públicas do `src/index.js` (fora do `AuthRouteHandler`). Elas não mostram estado de sessão
 * e rodam no mesmo navegador da recepção — a TV do chamador, o totem, a sala do paciente.
 */
const PUBLIC_EXACT_PATHS = ['/join', '/telehealth', '/botox-to-print'];
const PUBLIC_PATH_PREFIXES = ['/join/', '/botox-to-print/', '/prenatal-share/', '/chamador/', '/totem/', '/validation/'];

function isPublicPage(pathname: string): boolean {
	// O react-router casa sem diferenciar maiúsculas e aceita barra final; aqui também.
	const normalized = pathname.toLowerCase().replace(/(.)\/+$/, '$1');
	return PUBLIC_EXACT_PATHS.includes(normalized) || PUBLIC_PATH_PREFIXES.some(prefix => normalized.startsWith(prefix));
}

/**
 * Quando uma aba deve recarregar por uma mudança de sessão feita em OUTRA aba.
 *
 * O cookie de sessão e o localStorage são das abas todas. Recarregar é necessário quando a aba
 * ficaria mostrando um estado que as requisições já não seguem:
 * - o storage foi limpo (`key === null`: logout ou sessão expirada em outra aba) numa aba logada;
 * - o usuário mudou (`id`): esta aba mostraria o anterior enquanto as requisições vão como o novo;
 * - apareceu uma sessão numa aba que abriu deslogada (estava na tela de login).
 *
 * Fora disso, NÃO recarrega:
 * - a mesma pessoa entrando de novo noutra aba só troca o `SessionId`, e recarregar cortava gravação
 *   e texto não salvo. Recarregar a cada `SessionId` novo, junto com a tela de login limpando o
 *   storage, fazia duas abas desfazerem o login uma da outra em laço;
 * - página pública: recarregar a TV do chamador a cada logout/login da recepcionista tirava o
 *   desbloqueio de áudio, e os pacientes passavam a ser chamados sem som;
 * - storage limpo numa aba que já abriu deslogada: na tela de login só fazia perder o OTP digitado.
 */
export function shouldReloadOnStorageChange(key: string | null, sessionIdKey: string, authenticatedAtLoad: boolean, pathname: string): boolean {
	if (isPublicPage(pathname)) return false;
	if (key === null) return authenticatedAtLoad;
	if (key === 'id') return true;
	return key === sessionIdKey && !authenticatedAtLoad;
}
